import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ROUTES, API_DOMAIN, headers } from '../../../Reusable_Components/Config/apiConfig';
import { ToastContext } from '../../../../context/ToastContext';
import ConfirmModal from '../../../Reusable_Components/ConfirmModal';
import { useListState } from '../../../../hooks/useListState';
import * as listCache from '../../../../services/listCache';
import { fmtDate } from '../../../../utils/fmt';

function pageRange(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, current, current - 1, current + 1].filter(p => p >= 1 && p <= total));
  return [...set].sort((a, b) => a - b);
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const STAGE_COLORS = {
  'Prospecting': '#64748b', 'Qualification': '#3b82f6', 'Proposal': '#8b5cf6',
  'Negotiation': '#f59e0b', 'Closed-Won': '#22c55e', 'Closed-Lost': '#ef4444',
};
const STAGE_ICONS = {
  'Prospecting': '🔍', 'Qualification': '✔', 'Proposal': '📄',
  'Negotiation': '🤝', 'Closed-Won': '🏆', 'Closed-Lost': '✗',
};


const fmtKES = (n) => {
  const num = Number(n);
  if (!num || isNaN(num)) return '—';
  return 'KES ' + num.toLocaleString('en-KE');
};

const isPast = (dateStr) => dateStr && new Date(dateStr) < new Date();

const StageBadge = ({ value }) => {
  const color = STAGE_COLORS[value] || '#94a3b8';
  const icon = STAGE_ICONS[value] || '';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 99, whiteSpace: 'nowrap',
      background: color + '18', border: `1px solid ${color}40`,
      fontSize: 11, fontWeight: 600, color,
    }}>
      {icon && <span style={{ fontSize: 10 }}>{icon}</span>}
      {value || '—'}
    </span>
  );
};

const ProbBar = ({ value }) => {
  const n = Number(value) || 0;
  const color = n >= 75 ? '#22c55e' : n >= 50 ? '#3b82f6' : n >= 25 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 60, height: 5, borderRadius: 99, background: 'var(--border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${n}%`, background: color, borderRadius: 99 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color, minWidth: 32 }}>{n}%</span>
    </div>
  );
};

const SKEL = Array.from({ length: 6 });

const OpportunityListDetails = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [fetchVersion, setFetchVersion] = useState(0);
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  const [listState, setListState] = useListState({ search: '', page: 1, pageSize: 25 });
  const search = listState.search;
  const page = listState.page;
  const pageSize = listState.pageSize;
  const setSearch = (val) => setListState((s) => ({ ...s, search: val }));
  const setPage = (val) => setListState((s) => ({ ...s, page: val }));
  const changePageSize = (n) => setListState((s) => ({ ...s, pageSize: n, page: 1 }));

  const baseUrl = API_DOMAIN + API_ROUTES.opportunity;
  const refetch = useCallback(() => setFetchVersion(v => v + 1), []);

  useEffect(() => {
    let cancelled = false;
    const url = `${baseUrl}?page=${page}&page_size=${pageSize}`;

    const applyData = (data) => {
      const items = Array.isArray(data) ? data : (data.results || []);
      setOpportunities(items);
      setTotalItems(Array.isArray(data) ? items.length : (data.count ?? items.length));
    };

    // Stale-while-revalidate: instant render from cache, background refresh only if stale.
    const cached = listCache.get(url);
    if (cached) {
      applyData(cached.data);
      setLoading(false);
    } else {
      setLoading(true);
    }

    if (!cached || cached.isStale) {
      listCache
        .fetchDeduped(url, headers)
        .then(data => {
          if (cancelled) return;
          listCache.set(url, data);
          applyData(data);
        })
        .catch(() => {
          if (cancelled) return;
          if (!cached) toast?.addToast({ type: 'error', message: 'Failed to load opportunities.' });
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }

    return () => { cancelled = true; };
  }, [baseUrl, page, pageSize, fetchVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_DOMAIN}${API_ROUTES.opportunity}${id}/`, { headers });
      toast?.addToast({ type: 'success', message: 'Opportunity deleted.' });
      listCache.invalidate((u) => u.startsWith(baseUrl));
      refetch();
    } catch {
      toast?.addToast({ type: 'error', message: 'Failed to delete opportunity.' });
    }
  };

  // Client-side search filters within the current page
  const q = search.toLowerCase();
  const filtered = q
    ? opportunities.filter(o =>
        (o.opportunity_name || '').toLowerCase().includes(q) ||
        (o.stage || '').toLowerCase().includes(q) ||
        (o.company || '').toLowerCase().includes(q)
      )
    : opportunities;

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const pages = pageRange(page, totalPages);
  const showFrom = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const showTo = Math.min(page * pageSize, totalItems);
  const goTo = (p) => setPage(Math.max(1, Math.min(p, totalPages)));

  return (
    <div>
      <div className="card">
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 340 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }}>🔍</span>
            <input
              className="form-input"
              style={{ paddingLeft: 32 }}
              placeholder="Search by name, stage, company…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            {loading
              ? 'Loading…'
              : q
                ? `${filtered.length.toLocaleString()} match${filtered.length !== 1 ? 'es' : ''} on this page`
                : `${totalItems.toLocaleString()} opportunit${totalItems !== 1 ? 'ies' : 'y'}`}
          </span>
        </div>

        <div className="table-scroll">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Opportunity</th>
                <th>Stage</th>
                <th>Value</th>
                <th>Probability</th>
                <th>Close Date</th>
                <th>Priority</th>
                <th style={{ textAlign: 'right', width: 1 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? SKEL.map((_, i) => (
                    <tr key={i}>
                      {[60, 55, 40, 55, 45, 35, 80].map((w, j) => (
                        <td key={j}><div className="skeleton" style={{ height: 13, width: `${w}%`, borderRadius: 4 }} /></td>
                      ))}
                    </tr>
                  ))
                : filtered.length === 0
                  ? <tr><td colSpan={7}>
                      <div className="empty-state">
                        <div className="empty-state-icon-wrap">
                          {search ? (
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.35-4.35" />
                            </svg>
                          ) : (
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 8v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8" />
                              <path d="M3 8l2.5-4.5A1 1 0 0 1 6.37 3h11.26a1 1 0 0 1 .87.5L21 8" />
                              <path d="M3 8h18" /><path d="M9 12h6" />
                            </svg>
                          )}
                        </div>
                        <p className="empty-state-title">{search ? 'No matching records' : 'No opportunities yet'}</p>
                        <p className="empty-state-text">
                          {search ? `We couldn't find anything for "${search}" on this page.` : 'Click "New Opportunity" to create your first one.'}
                        </p>
                        {search && (
                          <button className="btn btn-secondary btn-sm" style={{ marginTop: 16 }} onClick={() => setSearch('')}>
                            Clear search
                          </button>
                        )}
                      </div>
                    </td></tr>
                  : filtered.map(opp => {
                    const overdue = isPast(opp.expected_close_date) && opp.stage !== 'Closed-Won' && opp.stage !== 'Closed-Lost';
                    return (
                      <tr key={opp.id}>
                        <td>
                          <div style={{ fontWeight: 600, color: 'var(--text-main)', fontSize: 13, maxWidth: 220 }}>
                            {opp.opportunity_name || `Opportunity #${opp.id}`}
                          </div>
                          {opp.company && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{opp.company}</div>
                          )}
                        </td>
                        <td><StageBadge value={opp.stage} /></td>
                        <td style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-main)', whiteSpace: 'nowrap' }}>
                          {fmtKES(opp.value)}
                        </td>
                        <td><ProbBar value={opp.probability_of_close} /></td>
                        <td>
                          <span style={{ fontSize: 12, color: overdue ? '#ef4444' : 'var(--text-muted)', fontWeight: overdue ? 600 : 400, whiteSpace: 'nowrap' }}>
                            {overdue && '⚠ '}{fmtDate(opp.expected_close_date)}
                          </span>
                        </td>
                        <td>
                          {opp.priority
                            ? <span style={{ fontSize: 11, fontWeight: 700, color: opp.priority === 'High' ? '#b91c1c' : opp.priority === 'Medium' ? '#a16207' : '#15803d' }}>
                                {opp.priority}
                              </span>
                            : <span style={{ color: 'var(--text-muted)' }}>—</span>
                          }
                        </td>
                        <td>
                          <div className="table-actions">
                            <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/opportunity/details/${btoa(opp.id)}`)}>
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/></svg>
                              View
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/opportunity/input/${btoa(opp.id)}`)}>
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z"/></svg>
                              Edit
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => setPendingDelete(opp.id)}>
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M2 4h12M6 4V3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1M5 4v9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V4"/></svg>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
              }
            </tbody>
          </table>
        </div>

        {!loading && totalItems > 0 && (
          <div className="pagination-wrap">
            <span className="pagination-info">
              {showFrom.toLocaleString()}–{showTo.toLocaleString()} of {totalItems.toLocaleString()}
            </span>

            <div className="pagination-buttons">
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(1)} disabled={page === 1} title="First">«</button>
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(page - 1)} disabled={page === 1} title="Previous">‹</button>
              {pages.map((p, idx) => {
                const prev = pages[idx - 1];
                return (
                  <React.Fragment key={p}>
                    {prev && p - prev > 1 && <span className="pagination-ellipsis">…</span>}
                    <button
                      className={`btn btn-sm pagination-page-btn${p === page ? ' active' : ''}`}
                      onClick={() => goTo(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })}
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(page + 1)} disabled={page === totalPages} title="Next">›</button>
              <button className="btn btn-secondary btn-sm" onClick={() => goTo(totalPages)} disabled={page === totalPages} title="Last">»</button>
            </div>

            <div className="pagination-size-wrap">
              <label className="pagination-size-label">Rows:</label>
              <select
                className="pagination-size-select"
                value={pageSize}
                onChange={e => changePageSize(Number(e.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        open={pendingDelete !== null}
        title="Delete Opportunity"
        message="Are you sure you want to delete this opportunity? This cannot be undone."
        confirmLabel="Delete"
        confirmDanger
        onConfirm={() => { handleDelete(pendingDelete); setPendingDelete(null); }}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default OpportunityListDetails;
